import { GetReturnManagementReq, toCompactDate } from '@/services/return/returnManagement/types'
import { FIELD_NAMES, ReturnManagementFormValues } from './constants'

/**
 * 폼 값에서 문자열 하나를 꺼낸다.
 *
 * `logistics.ShipperSelect` 화주명 칸은 배열로 온다. pbo-core 0.7.8 `FilterArea` 가 `nameMode` 를
 * `ShipperSelectField` 로 전달하지 않아 `nameMode: 'single'` 을 넘겨도 UI 는 다중 선택으로 그려지고
 * 값도 배열이 된다(gr02V2 `filterToRequest.ts` 에 같은 실측이 적혀 있다).
 *
 * 이걸 문자열로 가정하고 `.trim()` 을 부르면 조회 클릭이 `TypeError: a.trim is not a function` 으로
 * 죽고 API 까지 가지 못한다(LOGISPRD-12578). 그래서 배열·문자열 양쪽을 받는다.
 * BE `shipper` 는 단일값만 받으므로 배열이면 비어 있지 않은 첫 값을 쓴다.
 */
export const toSingleValue = (value?: string[] | string): string | undefined => {
  // String() 은 남는 캐스팅처럼 보이지만 의도한 방어다 — 이 함수가 존재하는 이유 자체가 선언 타입과
  // 런타임 값이 어긋나서다(gr02V2 `toTrimmedList` 도 같은 이유로 캐스팅을 남겨 뒀다).
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).find(Boolean)
  if (typeof value === 'string') return value.trim() || undefined
  return undefined
}

/**
 * 필터 폼 값 → 조회 요청.
 *
 * 빈 값은 싣지 않는다. 날짜는 `yyyyMMdd` 로 보낸다(BE 계약, 3.1). `rlChannel` 은 `allOption` 의
 * "전체" 가 빈 문자열이라 그 값이면 조건을 뺀다.
 */
export const toReturnManagementReq = (values: ReturnManagementFormValues): GetReturnManagementReq => {
  const [plnFrom, plnTo] = values[FIELD_NAMES.plnDateRange] ?? [null, null]
  const shipper = toSingleValue(values[FIELD_NAMES.shipperCodes])
  const insBy = toSingleValue(values[FIELD_NAMES.insBy])
  const rlChannel = toSingleValue(values[FIELD_NAMES.rlChannel])

  return {
    ...(shipper ? { shipper } : {}),
    ...(plnFrom ? { plnDateFrom: toCompactDate(plnFrom.format('YYYY-MM-DD')) } : {}),
    ...(plnTo ? { plnDateTo: toCompactDate(plnTo.format('YYYY-MM-DD')) } : {}),
    ...(insBy ? { insBy } : {}),
    ...(rlChannel ? { rlChannel } : {}),
  }
}

/**
 * 필수 필터 검증 — 화주 AND 반품일자(피드백 11). `FilterArea` 는 `rules` 를 조회 전에 검증하지 않아
 * 페이지가 직접 막는다.
 */
export const hasRequiredFilter = (req: GetReturnManagementReq): boolean =>
  Boolean(req.shipper) && Boolean(req.plnDateFrom) && Boolean(req.plnDateTo)
